"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REPORT_PERIODS = void 0;
exports.periodRange = periodRange;
exports.resolvedPeriod = resolvedPeriod;
exports.periodOf = periodOf;
const dates_1 = require("../dates");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The report period presets — `main.jsx` 207–214's `setRepPeriod`.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Here rather than in the screen, because it is date arithmetic with a predicate** — CLAUDE.md
 * §4's rule for what gets a unit test. The screen picks a label; this decides the two dates.
 *
 * ⚠️ **Every preset ends TODAY, not at the end of the calendar period, and that is the mockup's
 * own** — its `to = T` for week, month, quarter and year alike. "Ez a hónap" means the month so far;
 * a month-to-date figure against a full-month target would be the more common mistake, and reading
 * a report whose range extends into the future is the more obvious one.
 */
exports.REPORT_PERIODS = [
    'week',
    'month',
    'quarter',
    'year',
    /**
     * 17 Oct 2026 · the rolling ones — this month and the N−1 before it, from the first of the
     * earliest. Kockázatkezelés read *the last three months* and the old product's timelog sheets a
     * year; a timeline wants whole months at its left edge, so these start on a first.
     */
    'last3Months',
    'last6Months',
    'last12Months',
    'all',
    'custom',
];
/**
 * The range a preset resolves to, relative to `today`.
 *
 * ⚠️ **`today` is a parameter, not `new Date()`.** A function that reads the clock cannot be tested
 * for the quarter boundary, which is the one case anybody gets wrong — and the mockup's own
 * `Component.TODAY` is a constant for the same reason.
 *
 * ⚠️ `custom` returns nulls: it means "the author typed the dates", so there is nothing to compute.
 * A caller switching TO custom keeps whatever range was showing rather than clearing it.
 */
function periodRange(period, today) {
    if (period === 'all' || period === 'custom')
        return { from: null, to: null };
    const year = today.slice(0, 4);
    if (period === 'week') {
        /* ⚠️ Monday-based, via the one helper. `getDay()` is Sunday-based and a hand-rolled `- dow`
         * lands a week early every Sunday — the trap `wireMonday` exists to hold. */
        return { from: (0, dates_1.wireMonday)(today), to: today };
    }
    if (period === 'month')
        return { from: `${today.slice(0, 8)}01`, to: today };
    if (period === 'year')
        return { from: `${year}-01-01`, to: today };
    if (period === 'last3Months' || period === 'last6Months' || period === 'last12Months') {
        const back = period === 'last3Months' ? 2 : period === 'last6Months' ? 5 : 11;
        const at = Number(year) * 12 + Number(today.slice(5, 7)) - 1 - back;
        const fromYear = Math.floor(at / 12);
        const fromMonth = (at % 12) + 1;
        return { from: `${String(fromYear)}-${String(fromMonth).padStart(2, '0')}-01`, to: today };
    }
    /* Quarter: back to the first month of the containing quarter — 1, 4, 7 or 10. */
    const month = Number(today.slice(5, 7));
    const first = month - ((month - 1) % 3);
    return { from: `${year}-${String(first).padStart(2, '0')}-01`, to: today };
}
/**
 * ⚠️⚠️ 17 Oct 2026 · **A SAVED REPORT CAN NOW KEEP ITS PRESET, AND THEN THE PRESET WINS.** The
 * definition stored only dates, so a report saved as *"this month"* in October still read October
 * in December. `definition.period` keeps the preset; the server resolves it against today on every
 * run (`resolvedPeriod`), and the stored dates are only what it read when it was saved.
 */
function resolvedPeriod(definition, today) {
    const preset = definition.period;
    if (preset === undefined || preset === 'custom') {
        return { from: definition.periodFrom, to: definition.periodTo };
    }
    return periodRange(preset, today);
}
/**
 * Which preset a range corresponds to, or `custom`.
 *
 * ⚠️ **Derived rather than stored, because the definition stores DATES.** The mockup keeps
 * `repPeriod` in component state and loses it on reload — so an opened report showed "Egyéni" for a
 * range that was exactly this month. Recomputing means the label always matches the dates.
 */
function periodOf(range, today) {
    if (range.from === null && range.to === null)
        return 'all';
    for (const period of [
        'week',
        'month',
        'quarter',
        'year',
        'last3Months',
        'last6Months',
        'last12Months',
    ]) {
        const candidate = periodRange(period, today);
        if (candidate.from === range.from && candidate.to === range.to)
            return period;
    }
    return 'custom';
}
//# sourceMappingURL=periods.js.map